import React, { useContext, useMemo } from 'react';

// DEPENDENCIES
import omit from 'lodash/omit';
import { useSelector } from 'react-redux';

// GLOBAL HELPERS
import { getListData, ListContext } from 'helpers/getListData';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { INTERFACE, AM_FINANCE } from 'pathnames.js';

// GLOBAL FUNCTIONS
import { makePath } from 'functions';

// PARTS
import Main from 'parts/main/Main';
import Table, { Filters } from 'parts/table/Table';
import Title from 'parts/title/Title';

// LOCAL VARIABLES
import { ACTIONS, COLUMNS } from 'columns';

// LOCAL COMPONENTS
import OrderSelect from './tools/OrderSelect';

// MAIN COMPONENT
const ProductList = () => {
  // CONTEXT
  const { result } = useContext(ListContext) || {};

  // REDUX
  const isMobile = useSelector(state => state.mobile?.mobile);

  // MEMOS
  const sortedResult = useMemo(
    () =>
      result?.map(({ sale_type_category, products }) => ({
        title: sale_type_category,
        data: products
      })),
    [result]
  );

  const saleTypeCatgories = useMemo(
    () => result.filter(r => r?.products?.length).map(r => omit(r, 'products')),
    [result]
  );

  // RETURN
  return (
    <Main>
      <Title
        documentTitle="AM/Finance Products"
        title="AM/Finance Products"
        tools={
          <>
            <Title.AddProducts label={isMobile ? 'Add F&I' : 'Add Finance Products (F&I)'} productType={1} />
            <Title.AddProducts label={isMobile ? 'Add AM' : 'Add Aftermarket Products (AM)'} productType={0} />
            <OrderSelect saleTypeCatgories={saleTypeCatgories} />
          </>
        }
        className="align-items-center"
        rowClassName="flex flex-grow-1 ms-auto"
      />
      <Filters>
        <Filters.Button.Active />
        <Filters.Button.NewUsed />
        <Filters.Button
          name="saleTypeCategoryId"
          options={[
            {
              label: 'Retail',
              value: '1'
            },
            {
              label: 'Wholesale',
              value: '2'
            }
          ]}
        />
        <Filters.Button.ProductType />
        <Filters.Search name="productName" placeholder="Search by product name" />
        <Filters.Reset />
      </Filters>
      <Table.Sorted sortedResult={sortedResult} mobilePreviewKeys={['product_title', 'product_type']} />
    </Main>
  );
};

export const PRODUCTS_COLUMNS = [
  {
    ...COLUMNS.amProduct
  },
  {
    ...COLUMNS.newUsed,
    numeric: true
  },
  {
    key: 'product_title',
    label: 'Product Name',
    mobileLabel: 'Name'
  },
  {
    key: 'product_code',
    label: 'Product Code',
    className: 'text-center',
    width: 70
  },
  {
    ...COLUMNS.updatedOn,
    key: 'updated_on'
  },
  {
    ...COLUMNS.updatedBy,
    label: 'User'
  },
  COLUMNS.active,
  {
    key: 'sort_order',
    label: 'Order',
    className: 'text-center',
    width: 50
  }
];

// EXPORT
export default getListData(ProductList, {
  key: 'amFinanceProducts',
  path: makePath(INTERFACE.amFinance, AM_FINANCE.products),
  fetchArgs: {
    endpoint: ENDPOINTS.admin.amFinance.products.list
  },
  defaults: {
    startRow: null,
    isActive: '1'
  },
  columns: PRODUCTS_COLUMNS,
  actions: [ACTIONS.edit('dealer_amfinance_id')]
});
